import * as XLSX from 'xlsx';

const ACCEPTED_EXT = ['.csv', '.xlsx'];

export function isAcceptedGenericRcmFile(file) {
  if (!file?.name) return false;
  const lower = file.name.toLowerCase();
  return ACCEPTED_EXT.some((ext) => lower.endsWith(ext));
}

export function getGenericRcmAcceptedExtLabel() {
  return ACCEPTED_EXT.join(', ');
}

/**
 * @returns {Promise<{ fileType: string, sheetNames: string[], sheets: Record<string, string[][]> }>}
 */
export async function readGenericRcmWorkbook(file) {
  if (!isAcceptedGenericRcmFile(file)) {
    const err = new Error('Use .csv or .xlsx');
    err.userFacing = true;
    throw err;
  }

  const fileType = file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'xlsx';
  let workbook;

  if (fileType === 'csv') {
    const buf = await file.arrayBuffer();
    let text;
    try {
      text = new TextDecoder('utf-8', { fatal: true }).decode(buf);
    } catch {
      text = new TextDecoder('windows-1252').decode(buf);
    }
    workbook = XLSX.read(text, { type: 'string' });
  } else {
    const buf = await file.arrayBuffer();
    workbook = XLSX.read(buf, { type: 'array' });
  }

  const sheetNames = Array.isArray(workbook.SheetNames) ? workbook.SheetNames.filter(Boolean) : [];
  if (sheetNames.length === 0) {
    const err = new Error('No sheets found in this file.');
    err.userFacing = true;
    throw err;
  }

  const sheets = {};
  for (const name of sheetNames) {
    const sheet = workbook.Sheets[name];
    sheets[name] = sheet
      ? XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '', blankrows: true })
      : [];
  }

  return { fileType, sheetNames, sheets };
}

/** Suggest header row index: row with the most non-empty cells in the first 20 rows. */
export function suggestGenericHeaderRowIndex(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const limit = Math.min(list.length, 20);
  let bestIdx = 0;
  let bestScore = -1;
  for (let i = 0; i < limit; i++) {
    const row = list[i] || [];
    const score = row.filter((c) => String(c ?? '').trim() !== '').length;
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function getGenericHeaderCells(rows, headerRowIndex) {
  const row = (rows || [])[headerRowIndex] || [];
  return row.map((c) => (c == null ? '' : String(c)));
}

export function getGenericSampleCells(rows, headerRowIndex, colIndex, sampleCount = 3) {
  const out = [];
  const start = headerRowIndex + 1;
  for (let i = start; i < (rows || []).length && out.length < sampleCount; i++) {
    const cell = rows[i]?.[colIndex];
    const text = cell == null ? '' : String(cell).trim();
    if (text) out.push(text);
  }
  return out;
}
